/**
 * Chakra Architect — battle-state + basis-helpers (pure logica, geen React).
 *
 * De engine is een set pure functies die een nieuwe BattleState teruggeven
 * (geen mutatie in-place vanuit de UI; intern muteren de functies een kloon).
 * Zo blijft alles testbaar en kan de Zustand-store simpel `set(nieuweState)`.
 */
import type { Aanval, Baas, Kaart, KaartType, Origin, Status } from '../data/types';
import { HEADERS } from '../data/headers';

/* ------------------------------------------------------------------ */
/* Seedbare RNG (reproduceerbare gevechten/tests)                      */
/* ------------------------------------------------------------------ */

export interface RNG {
  seed: number;
}
/** mulberry32 — kleine, snelle PRNG. Geeft [0,1). Muteert state.seed. */
export function rngNext(rng: RNG): number {
  rng.seed |= 0;
  rng.seed = (rng.seed + 0x6d2b79f5) | 0;
  let t = Math.imul(rng.seed ^ (rng.seed >>> 15), 1 | rng.seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}
export function rngInt(rng: RNG, maxExclusief: number): number {
  return Math.floor(rngNext(rng) * maxExclusief);
}
export function rngShuffle<T>(rng: RNG, arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = rngInt(rng, i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ------------------------------------------------------------------ */
/* State-typen                                                         */
/* ------------------------------------------------------------------ */

/** Een kaart-instantie in dit gevecht (eigen instId, want id's zijn uniek maar
 * we willen stabiele identiteit voor bevriezen/selecteren). */
export interface KaartInst {
  instId: string;
  kaart: Kaart;
}

export interface ActieveStatus extends Status {
  /** resterende beurten (one-shot kinds: 1). */
  resterend: number;
}

export interface ActieveAura {
  block?: number;
  energy?: number;
  cleanseVeil?: boolean;
  name: string;
}

export interface SpelerState {
  hp: number;
  maxHp: number;
  energie: number;
  maxEnergie: number;
  /** Huidig schild (Block dat de volgende aanval absorbeert). */
  schild: number;
  /** Resterende baas-beurten dat het schild blijft staan. */
  schildTurns: number;
  /** Blokkeert de volgende aanval volledig (full:true / Aardeanker). */
  volledigBlok: boolean;
  /** Volgende aanval weerkaatsen naar iets positiefs (reflectNext). */
  reflectVolgende: boolean;
  /** Extra HP-bonus bij het weerkaatsen (reflectBonus uit een zwakte). */
  reflectBonus: number;
  statuses: ActieveStatus[];
  aura: ActieveAura | null;

  /* beurt-/duur-gebonden modifiers */
  buffNext: number;
  /** Output-buffs over meerdere beurten; `type` filtert op kaarttype (combo's). */
  buffOutput: { amt: number; resterend: number; type?: KaartType }[];
  costCutDeze: number;
  costDownType: { type: KaartType; amt: number; resterend: number }[];
  freeAll: boolean;
  freeTypes: KaartType[];
  freeNextCount: number;
  multDeze: { origin: Origin; amt: number }[];
  energieVolgende: number;
  healOverTime: { amt: number; resterend: number }[];
  veilImmuneTurns: number;
  veilPermanentCleared: boolean;

  /* tracking binnen de beurt / het gevecht */
  gespeeldDezeBeurt: Kaart[];
  typeTellingTotaal: Record<KaartType, number>;
  blockDezeBeurt: number;
  /** Combo-id's die deze beurt al zijn toegekend (idempotentie). */
  toegepasteCombos: string[];
  /** Duurste kaart in de starthand van deze beurt (voor conditionalDmg). */
  duursteStarthandCost: number;
}

export interface BaasState {
  baas: Baas;
  /** HP (gewone bazen) — undefined bij verbindingFight. */
  hp: number;
  /** Verbinding-meter (Theluun, §13). */
  verbinding: number;
  energie: number;
  /** Aangekondigde aanval voor deze speler-beurt (telegraferen). */
  intent: Aanval | null;
  verwijderd: string[];
  weakenAmt: number;
  weakenTurns: number;
  drainAmt: number;
  drainTurns: number;
  /** Kharos blokkade-stacks. */
  stacks: number;
  /** Baas slaat zijn volgende aanval over (Stilte/bossSkip). */
  slaOver: boolean;
}

export type Resultaat = 'bezig' | 'gewonnen' | 'verloren';

export interface BattleState {
  chakra: Origin;
  speler: SpelerState;
  baasState: BaasState;
  deck: KaartInst[];
  hand: KaartInst[];
  discard: KaartInst[];
  /** instId's van bevroren handkaarten (deze beurt onbruikbaar). */
  bevroren: string[];
  beurt: number;
  resultaat: Resultaat;
  log: string[];
  /** Mantra-frequenties (Hz) die sinds de laatste UI-tick gespeeld moeten worden. */
  tonen: number[];
  rng: RNG;
}

/* ------------------------------------------------------------------ */
/* Init                                                                */
/* ------------------------------------------------------------------ */

export const HANDGROOTTE = 5;

function leegTypeTelling(): Record<KaartType, number> {
  return { s: 0, o: 0, p: 0, e: 0, m: 0, k: 0 };
}

export interface InitOpties {
  /** Confrontatie-houding-bonus (1e beurt). */
  startBlock?: number;
  extraDraw?: number;
  /** Confronteren: de eerste aangekondigde aanval doet N minder schade. */
  eersteAanvalMin?: number;
  /** Beginwaarde van de RNG-seed. */
  seed?: number;
  /** HP waarmee de speler dit gevecht start (sub-bazen behouden HP, §1.2). */
  startHp?: number;
}

let instTeller = 0;
function maakInst(kaart: Kaart): KaartInst {
  instTeller += 1;
  return { instId: `${kaart.id}#${instTeller}`, kaart };
}

/** Bouwt een nieuw gevecht tegen `baas` met de gegeven kaartenpool. */
export function initBattle(chakra: Origin, baas: Baas, pool: Kaart[], opties: InitOpties = {}): BattleState {
  const header = HEADERS[chakra];
  const rng: RNG = { seed: (opties.seed ?? 12345) | 0 };
  const deck = rngShuffle(rng, pool.map(maakInst));

  const speler: SpelerState = {
    hp: opties.startHp ?? header.maxHP,
    maxHp: header.maxHP,
    energie: header.maxEnergie,
    maxEnergie: header.maxEnergie,
    schild: opties.startBlock ?? 0,
    schildTurns: opties.startBlock ? 1 : 0,
    volledigBlok: false,
    reflectVolgende: false,
    reflectBonus: 0,
    statuses: [],
    aura: null,
    buffNext: 0,
    buffOutput: [],
    costCutDeze: 0,
    costDownType: [],
    freeAll: false,
    freeTypes: [],
    freeNextCount: 0,
    multDeze: [],
    energieVolgende: 0,
    healOverTime: [],
    veilImmuneTurns: 0,
    veilPermanentCleared: false,
    gespeeldDezeBeurt: [],
    typeTellingTotaal: leegTypeTelling(),
    blockDezeBeurt: 0,
    toegepasteCombos: [],
    duursteStarthandCost: 0,
  };

  const baasState: BaasState = {
    baas,
    hp: baas.hp ?? 0,
    verbinding: baas.verbindingFight ? 50 : 0,
    energie: baas.energie,
    intent: null,
    verwijderd: [],
    weakenAmt: opties.eersteAanvalMin ?? 0,
    weakenTurns: opties.eersteAanvalMin ? 1 : 0,
    drainAmt: 0,
    drainTurns: 0,
    stacks: 0,
    slaOver: false,
  };

  const state: BattleState = {
    chakra,
    speler,
    baasState,
    deck,
    hand: [],
    discard: [],
    bevroren: [],
    beurt: 0,
    resultaat: 'bezig',
    log: [],
    tonen: [],
    rng,
  };

  // Eerste hand trekken (incl. eventuele bevraag-bonus).
  trek(state, HANDGROOTTE + (opties.extraDraw ?? 0));
  return state;
}

/* ------------------------------------------------------------------ */
/* Deck-helpers                                                        */
/* ------------------------------------------------------------------ */

/** Trekt n kaarten; herschudt discard→deck als het deck leeg raakt (§1.2). */
export function trek(state: BattleState, n: number): void {
  for (let i = 0; i < n; i++) {
    if (state.deck.length === 0) {
      if (state.discard.length === 0) return; // niets meer te trekken
      state.deck = rngShuffle(state.rng, state.discard);
      state.discard = [];
    }
    const kaart = state.deck.shift();
    if (kaart) state.hand.push(kaart);
  }
}

export function log(state: BattleState, regel: string): void {
  state.log.push(regel);
}

export function kloon(state: BattleState): BattleState {
  // Structurele kloon; RNG-seed wordt meegekopieerd zodat vervolg deterministisch is.
  return JSON.parse(JSON.stringify(state)) as BattleState;
}
